import { useState, useEffect } from 'react';
import { adminUsersAPI } from '../../lib/adminApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import {
  Users, Search, Trash2, Edit2, Loader2, Eye, Plus, Wallet,
  PhoneCall, Bot, Megaphone, Mail, Building2, UserPlus, CreditCard,
  ArrowUp, ArrowDown
} from 'lucide-react';
import { toast } from 'sonner';

const PLAN_COLORS = {
  free: 'bg-slate-800 text-slate-400 border-slate-700',
  standard: 'bg-emerald-600/20 text-emerald-400 border-emerald-600/30',
  premium: 'bg-amber-600/20 text-amber-400 border-amber-600/30',
  enterprise: 'bg-purple-600/20 text-purple-400 border-purple-600/30',
};

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ plan: 'free', usage_minutes: 0, usage_calls: 0, bonus_minutes: 0 });

  // Create User
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '', email: '', password: '', company: '', phone: '',
    plan: 'free', wallet_amount: 0, bonus_minutes: 0
  });

  // Wallet Management
  const [walletUser, setWalletUser] = useState(null);
  const [walletForm, setWalletForm] = useState({ amount: '', action: 'credit', description: '' });
  const [walletLoading, setWalletLoading] = useState(false);

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    try {
      const res = await adminUsersAPI.list();
      setUsers(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleViewUser = async (userId) => {
    try {
      const res = await adminUsersAPI.get(userId);
      setSelectedUser(res.data);
    } catch (err) { toast.error('Failed to load user details'); }
  };

  const handleEditUser = (user) => {
    setEditForm({
      plan: user.plan || 'free',
      usage_minutes: user.usage_minutes || 0,
      usage_calls: user.usage_calls || 0,
      bonus_minutes: user.bonus_minutes || 0
    });
    setEditUser(user);
  };

  const handleSaveUser = async () => {
    try {
      await adminUsersAPI.update(editUser.id, editForm);
      toast.success('User updated');
      setEditUser(null);
      loadUsers();
    } catch (err) { toast.error('Failed to update user'); }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Delete user "${userName}" and ALL their data (agents, campaigns, calls, wallet)? This cannot be undone.`)) return;
    try {
      await adminUsersAPI.delete(userId);
      toast.success('User deleted');
      loadUsers();
    } catch (err) { toast.error('Failed to delete user'); }
  };

  const handleCreateUser = async () => {
    if (!createForm.name || !createForm.email || !createForm.password) {
      toast.error('Name, email and password are required');
      return;
    }
    if (createForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setCreating(true);
    try {
      const res = await adminUsersAPI.create(createForm);
      toast.success(res.data.message);
      setShowCreate(false);
      setCreateForm({ name: '', email: '', password: '', company: '', phone: '', plan: 'free', wallet_amount: 0, bonus_minutes: 0 });
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally { setCreating(false); }
  };

  const handleWalletAction = async () => {
    const amt = parseFloat(walletForm.amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    setWalletLoading(true);
    try {
      const res = await adminUsersAPI.manageWallet(walletUser.id, {
        amount: amt,
        action: walletForm.action,
        description: walletForm.description
      });
      toast.success(res.data.message);
      setWalletUser(null);
      setWalletForm({ amount: '', action: 'credit', description: '' });
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Wallet operation failed');
    } finally { setWalletLoading(false); }
  };

  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.company?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-amber-500 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6 max-w-7xl" data-testid="admin-users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit'] text-white">User Management</h1>
          <p className="text-slate-400 mt-1">{users.length} registered users</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search users..."
              data-testid="admin-users-search" className="pl-10 bg-slate-950 border-white/10 text-white placeholder:text-slate-600" />
          </div>
          <Button onClick={() => setShowCreate(true)} data-testid="create-user-btn"
            className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap">
            <UserPlus className="w-4 h-4 mr-2" /> Add User
          </Button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="glass-card rounded-xl p-12 text-center">
          <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold font-['Outfit'] text-white mb-2">No users found</h3>
        </div>
      ) : (
        <div className="glass-card rounded-xl overflow-hidden" data-testid="admin-users-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-5 py-3">User</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-5 py-3">Company</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-5 py-3">Plan</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-5 py-3">Usage</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-5 py-3">Joined</th>
                  <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((user, i) => (
                  <tr key={user.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors animate-fade-in"
                    style={{ animationDelay: `${i * 30}ms` }} data-testid={`admin-user-row-${user.id}`}>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-amber-600/20 flex items-center justify-center text-amber-300 text-xs font-semibold">
                          {user.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <div className="text-sm text-white font-medium">{user.name}</div>
                          <div className="text-xs text-slate-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-400">{user.company || '-'}</td>
                    <td className="px-5 py-3.5">
                      <Badge className={`capitalize ${PLAN_COLORS[user.plan] || PLAN_COLORS.free}`}>{user.plan || 'free'}</Badge>
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-400">
                      {user.usage_calls || 0} calls / {user.usage_minutes || 0} min
                      {user.bonus_minutes > 0 && <span className="text-emerald-400 ml-1">(+{user.bonus_minutes})</span>}
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-500">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleViewUser(user.id)}
                          className="text-slate-400 hover:text-white hover:bg-white/5 text-xs" title="View Details">
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleEditUser(user)}
                          className="text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10 text-xs" title="Edit User">
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setWalletUser(user)}
                          className="text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10 text-xs" title="Manage Wallet">
                          <Wallet className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteUser(user.id, user.name)}
                          className="text-slate-400 hover:text-red-400 hover:bg-red-500/10 text-xs" title="Delete User">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== CREATE USER DIALOG ===== */}
      <Dialog open={showCreate} onOpenChange={(open) => { if (!open) setShowCreate(false); }}>
        <DialogContent className="max-w-lg bg-[#0B1221] border-white/10 text-white" data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-400" /> Create New User
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300 text-sm mb-2 block">Full Name *</Label>
                <Input value={createForm.name} onChange={(e) => setCreateForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="John Doe" data-testid="create-user-name"
                  className="bg-slate-950 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-slate-300 text-sm mb-2 block">Email *</Label>
                <Input value={createForm.email} onChange={(e) => setCreateForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="user@company.com" data-testid="create-user-email"
                  className="bg-slate-950 border-white/10 text-white" />
              </div>
            </div>
            <div>
              <Label className="text-slate-300 text-sm mb-2 block">Password *</Label>
              <Input type="password" value={createForm.password}
                onChange={(e) => setCreateForm(p => ({ ...p, password: e.target.value }))}
                placeholder="Min 6 characters" data-testid="create-user-password"
                className="bg-slate-950 border-white/10 text-white" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300 text-sm mb-2 block">Company</Label>
                <Input value={createForm.company} onChange={(e) => setCreateForm(p => ({ ...p, company: e.target.value }))}
                  placeholder="Company name" className="bg-slate-950 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-slate-300 text-sm mb-2 block">Phone</Label>
                <Input value={createForm.phone} onChange={(e) => setCreateForm(p => ({ ...p, phone: e.target.value }))}
                  placeholder="+91..." className="bg-slate-950 border-white/10 text-white" />
              </div>
            </div>
            <div>
              <Label className="text-slate-300 text-sm mb-2 block">Assign Plan</Label>
              <Select value={createForm.plan} onValueChange={(v) => setCreateForm(p => ({ ...p, plan: v }))}>
                <SelectTrigger className="bg-slate-950 border-white/10 text-white" data-testid="create-user-plan">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10">
                  {['free', 'standard', 'premium', 'enterprise'].map(p => (
                    <SelectItem key={p} value={p} className="text-white hover:bg-white/5 capitalize">{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300 text-sm mb-2 block">Initial Wallet (₹)</Label>
                <Input type="number" value={createForm.wallet_amount}
                  onChange={(e) => setCreateForm(p => ({ ...p, wallet_amount: parseFloat(e.target.value) || 0 }))}
                  placeholder="0" data-testid="create-user-wallet"
                  className="bg-slate-950 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-slate-300 text-sm mb-2 block">Bonus Minutes</Label>
                <Input type="number" value={createForm.bonus_minutes}
                  onChange={(e) => setCreateForm(p => ({ ...p, bonus_minutes: parseInt(e.target.value) || 0 }))}
                  placeholder="0" data-testid="create-user-bonus"
                  className="bg-slate-950 border-white/10 text-white" />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="ghost" onClick={() => setShowCreate(false)} className="text-slate-400 hover:text-white">Cancel</Button>
            <Button onClick={handleCreateUser} disabled={creating} data-testid="confirm-create-user"
              className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Create User
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== VIEW USER DIALOG ===== */}
      <Dialog open={!!selectedUser} onOpenChange={(open) => { if (!open) setSelectedUser(null); }}>
        <DialogContent className="max-w-md bg-[#0B1221] border-white/10 text-white" data-testid="view-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">User Details</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-amber-600/20 flex items-center justify-center text-amber-300 text-xl font-bold">
                  {selectedUser.name?.charAt(0)?.toUpperCase()}
                </div>
                <div>
                  <div className="text-lg font-semibold text-white">{selectedUser.name}</div>
                  <div className="text-sm text-slate-400">{selectedUser.email}</div>
                  {selectedUser.method && <Badge className="mt-1 bg-white/5 text-slate-500 border-white/10 text-[10px]">{selectedUser.method}</Badge>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Building2, label: 'Company', value: selectedUser.company || 'N/A' },
                  { icon: Users, label: 'Plan', value: selectedUser.plan || 'free' },
                  { icon: Bot, label: 'Agents', value: selectedUser.agents_count || 0 },
                  { icon: Megaphone, label: 'Campaigns', value: selectedUser.campaigns_count || 0 },
                  { icon: PhoneCall, label: 'Calls', value: selectedUser.calls_count || 0 },
                  { icon: Mail, label: 'Minutes Used', value: selectedUser.usage_minutes || 0 },
                  { icon: CreditCard, label: 'Bonus Min', value: selectedUser.bonus_minutes || 0 },
                  { icon: Wallet, label: 'Wallet', value: `Check wallet tab` },
                ].map((item, i) => (
                  <div key={i} className="bg-slate-950/50 rounded-lg p-3 border border-white/5">
                    <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                      <item.icon className="w-3 h-3" /> {item.label}
                    </div>
                    <div className="text-white font-medium text-sm capitalize">{item.value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== EDIT USER DIALOG ===== */}
      <Dialog open={!!editUser} onOpenChange={(open) => { if (!open) setEditUser(null); }}>
        <DialogContent className="max-w-md bg-[#0B1221] border-white/10 text-white" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Edit User: {editUser?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-300 text-sm mb-2 block">Plan</Label>
              <Select value={editForm.plan} onValueChange={(v) => setEditForm(p => ({ ...p, plan: v }))}>
                <SelectTrigger className="bg-slate-950 border-white/10 text-white" data-testid="edit-user-plan">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10">
                  {['free', 'standard', 'premium', 'enterprise'].map(p => (
                    <SelectItem key={p} value={p} className="text-white hover:bg-white/5 capitalize">{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-slate-300 text-sm mb-2 block">Minutes Used</Label>
                <Input type="number" value={editForm.usage_minutes}
                  onChange={(e) => setEditForm(p => ({ ...p, usage_minutes: parseInt(e.target.value) || 0 }))}
                  className="bg-slate-950 border-white/10 text-white" data-testid="edit-user-minutes" />
              </div>
              <div>
                <Label className="text-slate-300 text-sm mb-2 block">Calls</Label>
                <Input type="number" value={editForm.usage_calls}
                  onChange={(e) => setEditForm(p => ({ ...p, usage_calls: parseInt(e.target.value) || 0 }))}
                  className="bg-slate-950 border-white/10 text-white" data-testid="edit-user-calls" />
              </div>
              <div>
                <Label className="text-slate-300 text-sm mb-2 block">Bonus Min</Label>
                <Input type="number" value={editForm.bonus_minutes}
                  onChange={(e) => setEditForm(p => ({ ...p, bonus_minutes: parseInt(e.target.value) || 0 }))}
                  className="bg-slate-950 border-white/10 text-white" data-testid="edit-user-bonus" />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="ghost" onClick={() => setEditUser(null)} className="text-slate-400 hover:text-white">Cancel</Button>
            <Button onClick={handleSaveUser} data-testid="save-user-button" className="bg-amber-600 hover:bg-amber-700 text-white">
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== WALLET MANAGEMENT DIALOG ===== */}
      <Dialog open={!!walletUser} onOpenChange={(open) => { if (!open) setWalletUser(null); }}>
        <DialogContent className="max-w-md bg-[#0B1221] border-white/10 text-white" data-testid="wallet-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] flex items-center gap-2">
              <Wallet className="w-5 h-5 text-emerald-400" /> Manage Wallet: {walletUser?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-slate-950/50 rounded-lg p-4 border border-white/5 text-center">
              <div className="text-xs text-slate-500 mb-1">User: {walletUser?.email}</div>
              <div className="text-xs text-slate-500">Plan: <span className="text-amber-400 capitalize">{walletUser?.plan || 'free'}</span></div>
            </div>
            <div>
              <Label className="text-slate-300 text-sm mb-2 block">Action</Label>
              <Select value={walletForm.action} onValueChange={(v) => setWalletForm(p => ({ ...p, action: v }))}>
                <SelectTrigger className="bg-slate-950 border-white/10 text-white" data-testid="wallet-action-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10">
                  <SelectItem value="credit" className="text-emerald-400 hover:bg-white/5">
                    <span className="flex items-center gap-2"><ArrowDown className="w-3 h-3" /> Credit (Add Funds)</span>
                  </SelectItem>
                  <SelectItem value="debit" className="text-red-400 hover:bg-white/5">
                    <span className="flex items-center gap-2"><ArrowUp className="w-3 h-3" /> Debit (Remove Funds)</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300 text-sm mb-2 block">Amount (₹)</Label>
              <Input type="number" value={walletForm.amount}
                onChange={(e) => setWalletForm(p => ({ ...p, amount: e.target.value }))}
                placeholder="Enter amount" data-testid="wallet-amount-input"
                className="bg-slate-950 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-slate-300 text-sm mb-2 block">Description (optional)</Label>
              <Input value={walletForm.description}
                onChange={(e) => setWalletForm(p => ({ ...p, description: e.target.value }))}
                placeholder="e.g. Bonus credit, Refund, etc."
                className="bg-slate-950 border-white/10 text-white" />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="ghost" onClick={() => setWalletUser(null)} className="text-slate-400 hover:text-white">Cancel</Button>
            <Button onClick={handleWalletAction} disabled={walletLoading} data-testid="confirm-wallet-action"
              className={`text-white ${walletForm.action === 'credit' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}`}>
              {walletLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {walletForm.action === 'credit' ? 'Add Funds' : 'Deduct Funds'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
